"use client";

import { useEffect, useState } from "react";
import { ArrowDownIcon, ArrowUpIcon, MedalIcon } from "lucide-react";

import { clientGet } from "@/service/client-api/client-api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { SuperLink } from "@/components/common/super-link";
import {
  BadgeMark,
  formatBadgePeriod,
} from "@/components/exams/exam-ranking/badge-mark";

const BADGE_SLOTS = 5;

const fmt = (n: number) => n.toLocaleString("pl-PL");

const pluralUsers = (n: number) => (n === 1 ? "użytkownika" : "użytkowników");

const Jump = ({ jump, isNew }: { jump: number | null; isNew: boolean }) => {
  if (isNew) {
    return <span className="text-sm font-semibold text-sky-600">NOWY</span>;
  }
  if (!jump) return null;
  if (jump > 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-sm font-medium text-emerald-600">
        <ArrowUpIcon size={14} />
        {jump}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-0.5 text-sm font-medium text-rose-500">
      <ArrowDownIcon size={14} />
      {Math.abs(jump)}
    </span>
  );
};

export const RankingStatCard = ({
  statsHref,
  className,
}: {
  statsHref: string;
  className?: string;
}) => {
  const [data, setData] = useState<ExamRankingVM | null>(null);
  const [badges, setBadges] = useState<ExamRankingBadgeVM[]>([]);

  useEffect(() => {
    let active = true;
    clientGet("exam.ranking", {
      metric: "attempt_answers,sr_answers",
      period: "week",
      categoryId: 0,
    })
      .then((res: ExamRankingVM) => active && setData(res))
      .catch(() => {});
    clientGet("exam.ranking.badges")
      .then(
        (res: { badges: ExamRankingBadgeVM[] }) =>
          active && setBadges(res?.badges ?? []),
      )
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const me = data?.me ?? null;
  const letter = (me?.alias || "?").trim().charAt(0).toUpperCase();
  const preview = badges.slice(0, BADGE_SLOTS);

  return (
    <div
      className={cn(
        "flex flex-col rounded-2xl border border-zinc-200 bg-white p-4 text-zinc-900 sm:p-6",
        className,
      )}
    >
      <span className="text-lg font-bold">Ranking</span>

      <div className="mt-4 flex flex-1 flex-col items-center text-center">
        <span className="bg-remedium-blue ring-remedium-blue/10 flex size-16 items-center justify-center rounded-full text-xl font-semibold text-white ring-4">
          {letter}
        </span>

        {me?.inRanking ? (
          <>
            <span className="bg-remedium-blue/10 text-remedium-blue mt-3 inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold">
              {fmt(me.value)} pyt. · tydzień
            </span>
            <div className="mt-2 flex items-center gap-1.5">
              <span className="text-2xl font-bold">
                {me.position ? `#${me.position}` : "—"}
              </span>
              <Jump jump={me.jump} isNew={me.isNew} />
            </div>
            <span className="mt-1 text-xs text-zinc-400">
              z {fmt(data?.totalUsers ?? 0)}{" "}
              {pluralUsers(data?.totalUsers ?? 0)}
            </span>
          </>
        ) : (
          <span className="mt-3 text-sm text-zinc-400">
            Rozwiąż pytania, aby pojawić się w rankingu.
          </span>
        )}

        {/* Odznaki: zdobyte + puste sloty do BADGE_SLOTS. */}
        <div className="mt-5 flex items-center justify-center gap-2">
          {Array.from({ length: BADGE_SLOTS }).map((_, i) => {
            const badge = preview[i];
            return (
              <span
                key={i}
                className={cn(
                  "flex size-11 items-center justify-center rounded-full",
                  !badge && "bg-zinc-100",
                )}
              >
                {badge ? (
                  <BadgeMark
                    iconUrl={badge.iconUrl}
                    title={badge.title}
                    description={badge.description}
                    subtitle={formatBadgePeriod(
                      badge.period,
                      badge.periodKey,
                      badge.periodLabel,
                    )}
                    size={40}
                  />
                ) : (
                  <MedalIcon size={18} className="text-zinc-300" />
                )}
              </span>
            );
          })}
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-2">
        <Button variant="outline" className="w-full justify-center" asChild>
          <SuperLink href={statsHref}>
            Wszystkie odznaki
            <span className="text-zinc-400">{badges.length}</span>
          </SuperLink>
        </Button>
        <Button variant="outline" className="w-full justify-center" asChild>
          <SuperLink href={statsHref}>Cały ranking</SuperLink>
        </Button>
      </div>
    </div>
  );
};
